import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineGrid,
  InlineStack,
  Select,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import type { FindingsPage, ShopSettings } from '../lib/types.js';
import { AppPage } from '../components/Layout.js';
import { SeverityBadge } from '../components/SeverityBadge.js';
import { MetricCard } from '../components/MetricCard.js';
import { groupFindingsByProduct, type ProductIssueGroup } from '../lib/group-findings.js';

type InsertionMethod = 'append' | 'prepend';

/**
 * Design #07 — Bulk fix. Select products, preview disclosure label,
 * apply in batch.
 */
export function BulkFix(): JSX.Element {
  const { t } = useTranslation();
  const { scanId } = useParams<{ scanId: string }>();
  const navigate = useNavigate();

  const [groups, setGroups] = useState<ProductIssueGroup[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [disclosureText, setDisclosureText] = useState('');
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Undo eligibility — ISO timestamp when the 24h window closes, or null
  // if no live backups exist for this scan.
  const [undoExpiresAt, setUndoExpiresAt] = useState<string | null>(null);

  // Inline edit state
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const [savingText, setSavingText] = useState(false);

  // Method state
  const [method, setMethod] = useState<InsertionMethod>('append');
  const [editingMethod, setEditingMethod] = useState(false);

  useEffect(() => {
    if (!scanId) return;
    const ac = new AbortController();
    (async () => {
      try {
        const [f, s, u] = await Promise.all([
          api.get<FindingsPage>(`/api/scan/${scanId}/findings?limit=100`, ac.signal),
          api.get<{ settings: ShopSettings }>('/api/settings', ac.signal),
          api.get<{ eligible: boolean; expiresAt?: string }>(
            `/api/fix/undo-status/${scanId}`,
            ac.signal,
          ),
        ]);
        const unfixed = f.items.filter((i) => !i.fixApplied && !i.locked);
        const grouped = groupFindingsByProduct(unfixed);
        setGroups(grouped);
        setSelected(new Set(grouped.map((g) => g.productId)));
        setDisclosureText(s.settings.disclosureText);
        if (u.eligible && u.expiresAt) {
          setUndoExpiresAt(u.expiresAt);
          setDone(true); // previously-applied scan — show the success + undo banner
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [scanId]);

  const toggle = useCallback(
    (productId: string) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(productId)) next.delete(productId);
        else next.add(productId);
        return next;
      }),
    [],
  );

  const selectAll = useCallback(() => setSelected(new Set(groups.map((g) => g.productId))), [groups]);
  const deselectAll = useCallback(() => setSelected(new Set()), []);

  const startEditing = useCallback(() => {
    setEditDraft(disclosureText);
    setEditing(true);
  }, [disclosureText]);

  const saveDisclosureText = useCallback(async () => {
    if (!editDraft.trim() || editDraft.length < 5) return;
    setSavingText(true);
    try {
      await api.put('/api/settings', { disclosureText: editDraft });
      setDisclosureText(editDraft);
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingText(false);
    }
  }, [editDraft]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setEditDraft('');
  }, []);

  const applyBulk = useCallback(async () => {
    if (!scanId) return;
    if (selected.size === 0) return;
    setApplying(true);
    setError(null);
    try {
      await api.post(`/api/fix/bulk/${scanId}`, {
        productIds: Array.from(selected),
        method,
      });
      setDone(true);
      setUndone(false);
      // Optimistic — backend only creates the backup row after the worker
      // starts processing. Worst case the countdown is off by a few
      // seconds; refresh on next mount pulls the real expiresAt.
      setUndoExpiresAt(new Date(Date.now() + 24 * 60 * 60_000).toISOString());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setApplying(false);
    }
  }, [scanId, selected, method]);

  const undoBulk = useCallback(async () => {
    if (!scanId) return;
    setUndoing(true);
    setError(null);
    try {
      await api.post(`/api/fix/undo/bulk/${scanId}`);
      setUndone(true);
      setDone(false);
      setUndoExpiresAt(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUndoing(false);
    }
  }, [scanId]);

  // Hours remaining in the 24h undo window. Recomputed on every render —
  // cheap and avoids setInterval churn; user reloads rarely sit open past
  // the window boundary.
  const undoHoursLeft = useMemo(() => {
    if (!undoExpiresAt) return null;
    const ms = new Date(undoExpiresAt).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.max(1, Math.ceil(ms / (60 * 60_000)));
  }, [undoExpiresAt]);

  const criticalCount = useMemo(() => groups.filter((g) => g.severity === 'critical').length, [groups]);
  const warningCount = useMemo(() => groups.filter((g) => g.severity === 'warning').length, [groups]);

  if (loading) {
    return (
      <AppPage
        title={t('bulkFix.title')}
        backAction={{ content: t('productDetail.back'), onAction: () => navigate('/', done ? { state: { justFixed: true } } : undefined) }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner accessibilityLabel="Loading" size="large" />
        </div>
      </AppPage>
    );
  }

  if (groups.length === 0) {
    return (
      <AppPage
        title={t('bulkFix.title')}
        backAction={{ content: t('productDetail.back'), onAction: () => navigate('/', done ? { state: { justFixed: true } } : undefined) }}
      >
        <BlockStack gap="400">
          {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
          <Banner tone="success" title={t('bulkFix.allFixedTitle')}>
            <p>{t('bulkFix.allFixedBody')}</p>
          </Banner>
          <InlineStack gap="200" align="end">
            <Button onClick={() => navigate('/', done ? { state: { justFixed: true } } : undefined)}>{t('productDetail.back')}</Button>
          </InlineStack>
        </BlockStack>
      </AppPage>
    );
  }

  return (
    <AppPage
      title={t('bulkFix.title')}
      subtitle={t('bulkFix.subtitle')}
      backAction={{ content: t('productDetail.back'), onAction: () => navigate('/', done ? { state: { justFixed: true } } : undefined) }}
    >
      <BlockStack gap="400">
        {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
        {done && (
          <Banner
            tone="success"
            action={
              undoHoursLeft !== null && undoHoursLeft > 0
                ? {
                    content: t('bulkFix.undoAction'),
                    onAction: () => void undoBulk(),
                    loading: undoing,
                  }
                : undefined
            }
          >
            <p>{t('bulkFix.applyingBody')}</p>
            {undoHoursLeft !== null && undoHoursLeft > 0 ? (
              <p style={{ marginTop: 6 }}>
                {t('bulkFix.undoNoticeWithCountdown', { hours: undoHoursLeft })}
              </p>
            ) : (
              <p style={{ marginTop: 6 }}>{t('bulkFix.undoExpired')}</p>
            )}
          </Banner>
        )}
        {undone && (
          <Banner tone="info" onDismiss={() => setUndone(false)}>
            <p>{t('bulkFix.undoneBody')}</p>
          </Banner>
        )}

        {/* Metric cards */}
        <InlineGrid columns={3} gap="300">
          <MetricCard label={t('bulkFix.criticalProducts')} value={criticalCount} tone="critical" center />
          <MetricCard label={t('bulkFix.warningProducts')} value={warningCount} tone="warning" center />
          <MetricCard label={t('bulkFix.selectedForFix')} value={selected.size} center />
        </InlineGrid>

        {/* Disclosure label card */}
        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">{t('bulkFix.disclosureText')}</Text>
            {editing ? (
              <BlockStack gap="200">
                <TextField
                  label={t('bulkFix.editDisclosureLabel')}
                  labelHidden
                  value={editDraft}
                  onChange={setEditDraft}
                  autoComplete="off"
                  multiline={2}
                  helpText={t('bulkFix.editDisclosureHint')}
                />
                <InlineStack gap="200" align="end">
                  <Button onClick={cancelEditing}>{t('bulkFix.cancel')}</Button>
                  <Button
                    variant="primary"
                    loading={savingText}
                    disabled={!editDraft.trim() || editDraft.length < 5}
                    onClick={saveDisclosureText}
                  >
                    {t('settings.save')}
                  </Button>
                </InlineStack>
              </BlockStack>
            ) : (
              <div style={{
                background: 'var(--p-color-bg-surface-secondary)', borderRadius: 8,
                padding: '12px 16px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 8,
              }}>
                <div style={{ fontSize: 13, fontStyle: 'italic' }}>{disclosureText}</div>
                <Button variant="plain" onClick={startEditing}>Edit</Button>
              </div>
            )}
            {editingMethod ? (
              <div style={{ maxWidth: 360 }}>
                <Select
                  label={t('bulkFix.methodLabel')}
                  labelHidden
                  options={[
                    { label: t('bulkFix.methodAppend'), value: 'append' },
                    { label: t('bulkFix.methodPrepend'), value: 'prepend' },
                  ]}
                  value={method}
                  onChange={(v) => { setMethod(v as InsertionMethod); setEditingMethod(false); }}
                />
              </div>
            ) : (
              <Text as="p" variant="bodySm" tone="subdued">
                {method === 'prepend' ? t('bulkFix.methodPrepend') : t('bulkFix.disclosureMethod')}{' · '}
                <Button variant="plain" size="micro" onClick={() => setEditingMethod(true)}>{t('bulkFix.changeMethod')}</Button>
              </Text>
            )}
          </BlockStack>
        </Card>

        {/* Preview card */}
        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">{t('bulkFix.previewTitle')}</Text>
            <Text as="p" variant="bodySm" tone="subdued">{t('bulkFix.previewDesc')}</Text>
            <div style={{
              background: 'var(--p-color-bg-surface-secondary)',
              border: '1px solid var(--p-color-border)',
              borderRadius: 8, padding: '14px 16px',
            }}>
              {method === 'prepend' && (
                <div style={{
                  borderBottom: '1px solid #e0e0e0', paddingBottom: 10, marginBottom: 6,
                  fontSize: 12, color: '#777', lineHeight: 1.5, fontStyle: 'italic',
                }}>
                  {disclosureText}
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{t('bulkFix.previewProductDesc')}</div>
              <div style={{ fontSize: 13, color: 'var(--p-color-text-subdued)', lineHeight: 1.7 }}>
                {t('bulkFix.previewSampleText')}
              </div>
              {method === 'append' && (
                <div style={{
                  borderTop: '1px solid #e0e0e0', paddingTop: 10, marginTop: 4,
                  fontSize: 12, color: '#777', lineHeight: 1.5, fontStyle: 'italic',
                }}>
                  {disclosureText}
                </div>
              )}
            </div>
          </BlockStack>
        </Card>

        {/* Products list */}
        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">{t('bulkFix.productsToUpdate')}</Text>

            {/* Header row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 0', borderBottom: '2px solid var(--p-color-border)',
            }}>
              <InlineStack gap="200" blockAlign="center">
                <Checkbox
                  label=""
                  labelHidden
                  checked={selected.size === groups.length}
                  onChange={selected.size === groups.length ? deselectAll : selectAll}
                />
                <Text as="span" variant="bodySm" fontWeight="semibold">
                  {t('bulkFix.selectedCount', { count: selected.size, total: groups.length })}
                </Text>
              </InlineStack>
              <InlineStack gap="200">
                <Button variant="plain" size="micro" onClick={selectAll}>{t('bulkFix.selectAll')}</Button>
                <Button variant="plain" size="micro" onClick={deselectAll}>{t('bulkFix.deselectAll')}</Button>
              </InlineStack>
            </div>

            {/* Product rows */}
            {groups.map((g) => (
              <div
                key={g.productId}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 10, padding: '10px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13,
                }}
              >
                <Checkbox
                  label={g.productTitle}
                  checked={selected.has(g.productId)}
                  onChange={() => toggle(g.productId)}
                />
                <SeverityBadge severity={g.severity} />
              </div>
            ))}
          </BlockStack>
        </Card>

        {/* Undo notice — matches design/screens/07-bulk-fix.html */}
        <div style={{
          background: '#F0F5FF', border: '1px solid #A3C3E8', borderRadius: 12,
          padding: '14px 16px',
        }}>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: '#10409E' }}>
            {t('bulkFix.undoNotice')}
          </div>
        </div>

        {/* Bottom action bar */}
        <Card>
          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" tone="subdued" variant="bodySm">
              {t('bulkFix.productsWillUpdate', { count: selected.size })}
            </Text>
            <InlineStack gap="200">
              <Button onClick={() => navigate('/', done ? { state: { justFixed: true } } : undefined)}>{t('bulkFix.cancel')}</Button>
              <BluePrimaryBtn
                loading={applying}
                disabled={selected.size === 0 || done}
                onClick={applyBulk}
              >
                {t('bulkFix.applyN')}
              </BluePrimaryBtn>
            </InlineStack>
          </InlineStack>
        </Card>
      </BlockStack>
    </AppPage>
  );
}

/** Blue primary button matching design .btn-primary (#005BD3) */
function BluePrimaryBtn({ children, onClick, loading, disabled }: {
  children: React.ReactNode; onClick: () => void; loading?: boolean; disabled?: boolean;
}): JSX.Element {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onClick={onClick} disabled={loading || disabled}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: hover && !disabled ? '#004BB0' : '#005BD3', color: '#fff', border: 'none',
        borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
        opacity: disabled ? 0.5 : 1, transition: 'background 0.15s',
      }}>
      {loading ? 'Applying…' : children}
    </button>
  );
}
